import { createContext, useContext, useState, useCallback, ReactNode } from 'react';

export type DialogVariant = 'info' | 'success' | 'warning' | 'danger' | 'error';

export interface DialogOptions {
  title: string;
  message: string;
  variant?: DialogVariant;
  confirmLabel?: string;
  cancelLabel?: string;
}

export interface ConfirmOptions extends DialogOptions {
  confirmLabel?: string;
  cancelLabel?: string;
}

export interface AlertOptions extends DialogOptions {
  confirmLabel?: string;
}

interface DialogState {
  isOpen: boolean;
  type: 'alert' | 'confirm';
  options: DialogOptions;
  resolve: ((value: boolean) => void) | null;
}

interface DialogContextValue {
  dialogState: DialogState | null;
  confirm: (options: ConfirmOptions) => Promise<boolean>;
  alert: (options: AlertOptions) => Promise<void>;
  closeDialog: () => void;
}

const DialogContext = createContext<DialogContextValue | undefined>(undefined);

export function DialogProvider({ children }: { children: ReactNode }) {
  const [dialogState, setDialogState] = useState<DialogState | null>(null);

  // Confirm dialog - returns boolean promise
  const confirm = useCallback((options: ConfirmOptions): Promise<boolean> => {
    return new Promise((resolve) => {
      setDialogState({
        isOpen: true,
        type: 'confirm',
        options: {
          variant: 'info',
          confirmLabel: 'Confirm',
          cancelLabel: 'Cancel',
          ...options,
        },
        resolve,
      });
    });
  }, []);

  // Alert dialog - returns void promise
  const alert = useCallback((options: AlertOptions): Promise<void> => {
    return new Promise((resolve) => {
      setDialogState({
        isOpen: true,
        type: 'alert',
        options: {
          variant: 'info',
          confirmLabel: 'OK',
          ...options,
        },
        resolve: (value: boolean) => {
          resolve();
          return value;
        },
      });
    });
  }, []);

  // Close dialog and resolve promise
  const closeDialog = useCallback(() => {
    setDialogState(null);
  }, []);

  const handleConfirm = useCallback(() => {
    if (dialogState?.resolve) {
      dialogState.resolve(true);
    }
    closeDialog();
  }, [dialogState, closeDialog]);

  const handleCancel = useCallback(() => {
    if (dialogState?.resolve) {
      dialogState.resolve(false);
    }
    closeDialog();
  }, [dialogState, closeDialog]);

  return (
    <DialogContext.Provider value={{ dialogState, confirm, alert, closeDialog }}>
      {children}
      {dialogState?.isOpen && (
        <DialogComponent
          type={dialogState.type}
          options={dialogState.options}
          onConfirm={handleConfirm}
          onCancel={handleCancel}
        />
      )}
    </DialogContext.Provider>
  );
}

export function useDialog() {
  const context = useContext(DialogContext);
  if (!context) {
    throw new Error('useDialog must be used within a DialogProvider');
  }
  return context;
}

// Dialog component with accessibility features
interface DialogComponentProps {
  type: 'alert' | 'confirm';
  options: DialogOptions;
  onConfirm: () => void;
  onCancel: () => void;
}

function DialogComponent({ type, options, onConfirm, onCancel }: DialogComponentProps) {
  const { title, message, variant = 'info', confirmLabel, cancelLabel } = options;

  // Handle escape key
  const handleKeyDown = (e: React.KeyboardEvent) => {
    if (e.key === 'Escape') {
      if (type === 'confirm') {
        onCancel();
      } else {
        onConfirm();
      }
    }
  };

  // Variant styling
  const getVariantStyles = () => {
    switch (variant) {
      case 'danger':
      case 'error':
        return {
          icon: (
            <svg className="w-6 h-6 text-red-600" fill="currentColor" viewBox="0 0 20 20">
              <path fillRule="evenodd" d="M10 18a8 8 0 100-16 8 8 0 000 16zM8.707 7.293a1 1 0 00-1.414 1.414L8.586 10l-1.293 1.293a1 1 0 101.414 1.414L10 11.414l1.293 1.293a1 1 0 001.414-1.414L11.414 10l1.293-1.293a1 1 0 00-1.414-1.414L10 8.586 8.707 7.293z" clipRule="evenodd" />
            </svg>
          ),
          bgColor: 'bg-red-50',
          borderColor: 'border-red-200',
          textColor: 'text-red-800',
          buttonClass: 'bg-red-600 text-white border-red-700 hover:bg-red-700',
        };
      case 'warning':
        return {
          icon: (
            <svg className="w-6 h-6 text-yellow-600" fill="currentColor" viewBox="0 0 20 20">
              <path fillRule="evenodd" d="M8.257 3.099c.765-1.36 2.722-1.36 3.486 0l5.58 9.92c.75 1.334-.213 2.98-1.742 2.98H4.42c-1.53 0-2.493-1.646-1.743-2.98l5.58-9.92zM11 13a1 1 0 11-2 0 1 1 0 012 0zm-1-8a1 1 0 00-1 1v3a1 1 0 002 0V6a1 1 0 00-1-1z" clipRule="evenodd" />
            </svg>
          ),
          bgColor: 'bg-yellow-50',
          borderColor: 'border-yellow-200',
          textColor: 'text-yellow-800',
          buttonClass: 'bg-yellow-600 text-white border-yellow-700 hover:bg-yellow-700',
        };
      case 'success':
        return {
          icon: (
            <svg className="w-6 h-6 text-green-600" fill="currentColor" viewBox="0 0 20 20">
              <path fillRule="evenodd" d="M10 18a8 8 0 100-16 8 8 0 000 16zm3.707-9.293a1 1 0 00-1.414-1.414L9 10.586 7.707 9.293a1 1 0 00-1.414 1.414l2 2a1 1 0 001.414 0l4-4z" clipRule="evenodd" />
            </svg>
          ),
          bgColor: 'bg-green-50',
          borderColor: 'border-green-200',
          textColor: 'text-green-800',
          buttonClass: 'bg-green-600 text-white border-green-700 hover:bg-green-700',
        };
      default: // info
        return {
          icon: (
            <svg className="w-6 h-6 text-blue-600" fill="currentColor" viewBox="0 0 20 20">
              <path fillRule="evenodd" d="M18 10a8 8 0 11-16 0 8 8 0 0116 0zm-7-4a1 1 0 11-2 0 1 1 0 012 0zM9 9a1 1 0 000 2v3a1 1 0 001 1h1a1 1 0 100-2v-3a1 1 0 00-1-1H9z" clipRule="evenodd" />
            </svg>
          ),
          bgColor: 'bg-blue-50',
          borderColor: 'border-blue-200',
          textColor: 'text-blue-800',
          buttonClass: 'btn-primary',
        };
    }
  };

  const styles = getVariantStyles();

  return (
    <div
      className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4"
      role="dialog"
      aria-modal="true"
      aria-labelledby="dialog-title"
      aria-describedby="dialog-message"
      onKeyDown={handleKeyDown}
    >
      <div className="card p-6 max-w-md w-full">
        <div className={`flex items-start gap-4 mb-4 p-4 rounded-lg ${styles.bgColor} ${styles.borderColor} border`}>
          <div className="flex-shrink-0">{styles.icon}</div>
          <div className="flex-1">
            <h2 id="dialog-title" className={`text-lg font-semibold ${styles.textColor} mb-2`}>
              {title}
            </h2>
            <p id="dialog-message" className={`text-sm ${styles.textColor}`}>
              {message}
            </p>
          </div>
        </div>

        <div className="flex gap-3 justify-end">
          {type === 'confirm' && (
            <button
              type="button"
              onClick={onCancel}
              className="btn"
              autoFocus={variant !== 'danger' && variant !== 'error'}
            >
              {cancelLabel}
            </button>
          )}
          <button
            type="button"
            onClick={onConfirm}
            className={`btn ${styles.buttonClass}`}
            autoFocus={type === 'alert' || variant === 'danger' || variant === 'error'}
          >
            {confirmLabel}
          </button>
        </div>
      </div>
    </div>
  );
}
